import type { GroupBy } from "../../domain/app-types.ts";
import type { SortDirection, TaskSort, Todo } from "../../domain/entities.ts";
import { escapeHtml } from "../../ui/text.ts";
import { showTaskContextBar } from "./list-navigation.ts";
import { taskPagerView } from "./list-presentation.ts";
import { compareTasks } from "./selectors.ts";

export interface TaskListCollectionViewOptions {
  todos: Todo[];
  view: string;
  grouping: GroupBy;
  categorySelected: boolean;
  showCompleted: boolean;
  searched: boolean;
  contextTitle: string;
  sortBy: TaskSort;
  sortDirection: SortDirection;
  pageSize: number;
  page: number;
  emptyHtml: string;
  renderRow(todo: Todo): string;
  activeWhen?(todo: Todo): boolean;
  groupLabel(todo: Todo, grouping: GroupBy): string;
  groupKey?(todo: Todo, grouping: GroupBy): string;
  icons: { left: string; right: string; up: string; down: string };
}

export interface TaskListCollectionViewResult {
  html: string;
  page: number;
}

function contextBar(options: TaskListCollectionViewOptions, count: number, groups: string[]): string {
  const filtered = options.view === "custom";
  if (!showTaskContextBar(count, options.searched, filtered, groups.length)) return "";
  return `<div class="task-context-bar"><div><strong>${escapeHtml(options.contextTitle)}</strong><span>${count} ${count === 1 ? "task" : "tasks"}${options.searched ? " · searched" : filtered ? " · filtered" : ""}</span></div>${groups.length >= 3 ? `<label>Jump to<select data-group-jump><option value="">Section…</option>${groups.map((name, index) => `<option value="task-group-${index}">${escapeHtml(name)}</option>`).join("")}</select></label>` : ""}<button class="icon-button" data-action="scroll-bottom" aria-label="Jump to bottom" title="Jump to bottom">${options.icons.down}</button></div>`;
}

export function renderTaskListCollection(options: TaskListCollectionViewOptions): TaskListCollectionViewResult {
  if (!options.todos.length) return { html: `<div class="todo-list" aria-live="polite">${options.emptyHtml}</div>`, page: 1 };
  if (options.view === "myday") return { html: `<div class="todo-list my-day-list" aria-live="polite">${options.todos.map(options.renderRow).join("")}</div>`, page: 1 };
  const sortedNormally = (items: Todo[]) => [...items].sort((left, right) => compareTasks(left, right, options.sortBy, options.sortDirection));
  const isActive = (todo: Todo) => !todo.completed || !!options.activeWhen?.(todo);
  const sorted = options.showCompleted ? [...sortedNormally(options.todos.filter(isActive)), ...sortedNormally(options.todos.filter(todo => !isActive(todo)))] : sortedNormally(options.todos);
  const pages = options.pageSize ? Math.max(1, Math.ceil(sorted.length / options.pageSize)) : 1;
  const page = Math.min(Math.max(1, options.page), pages);
  const paged = options.pageSize ? sorted.slice((page - 1) * options.pageSize, page * options.pageSize) : sorted;
  const pager = options.pageSize && pages > 1 ? taskPagerView(page, pages, sorted.length, options.icons) : "";
  const backToTop = `<button class="back-to-top" data-action="scroll-top" aria-label="Back to top" title="Back to top">${options.icons.up}</button>`;
  if (options.grouping === "none" || options.view !== "custom" && options.grouping !== "epic") {
    const active = paged.filter(isActive);
    const completed = paged.filter(todo => !isActive(todo));
    const pinned = active.filter(todo => todo.pinned);
    const regular = active.filter(todo => !todo.pinned);
    const useSections = options.showCompleted || (options.categorySelected && options.view === "open" && pinned.length > 0);
    const section = (label: string, items: Todo[], kind: string) => items.length ? `<section class="task-list-section task-list-section--${kind}"><header><span>${label}</span><small>${items.length}</small></header><div class="todo-list" aria-label="${label} tasks in this collection">${items.map(options.renderRow).join("")}</div></section>` : "";
    const rows = useSections
      ? `<div class="task-list-sections" aria-live="polite">${section("Pinned", pinned, "pinned")}${section("Tasks", regular, "regular")}${options.showCompleted ? section("Completed", completed, "completed") : ""}</div>`
      : `<div class="todo-list" aria-live="polite">${paged.map(options.renderRow).join("")}</div>`;
    return { html: `${contextBar(options, sorted.length, [])}${pager ? `<div class="task-pagination--top">${pager}</div>` : ""}${rows}${pager}${backToTop}`, page };
  }
  const groups = new Map<string, Todo[]>();
  paged.forEach(todo => { const key = options.groupKey?.(todo, options.grouping) || options.groupLabel(todo, options.grouping); groups.set(key, [...(groups.get(key) || []), todo]); });
  const entries = [...groups.entries()].map(([key, tasks]) => [options.groupLabel(tasks[0], options.grouping), tasks] as const).sort(([left], [right]) => left.localeCompare(right));
  return { html: `${contextBar(options, sorted.length, entries.map(([name]) => name))}${pager ? `<div class="task-pagination--top">${pager}</div>` : ""}<div class="grouped-tasks" aria-live="polite">${entries.map(([name, tasks], index) => `<section class="task-group" id="task-group-${index}"><header><h2>${escapeHtml(name)}</h2><span>${tasks.length}</span></header><div class="todo-list">${tasks.map(options.renderRow).join("")}</div></section>`).join("")}</div>${pager}${backToTop}`, page };
}
